import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { CriterionScoreAnalysis } from "../../types"

import { ChevronDown, ChevronRight } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import {
  CitationText,
  type ExtractionItemLookup,
  type OpenSourceFn,
  SourceSnippetPill,
} from "./extraction-citation"

// ── Types ─────────────────────────────────────────────────────────────────────

type ScoringTabProps = {
  criterionScores?: CriterionScoreAnalysis[]
  totalPredictedScore?: number
  totalMaxScore?: number
  scoringNotes?: string
  isLoading: boolean
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 4) return "bg-success-ink"
  if (score >= 3) return "bg-warning-ink"
  return "bg-destructive-ink"
}

function scoreLabelColor(score: number): string {
  if (score >= 4) return "text-success"
  if (score >= 3) return "text-warning-foreground"
  return "text-destructive"
}

function formatContribution(contribution: number): string {
  return contribution % 1 === 0 ? `${contribution}` : contribution.toFixed(1)
}

// ── Criterion row ─────────────────────────────────────────────────────────────

function CriterionRow({
  criterion,
  extractionLookup,
  onOpenSource,
}: {
  criterion: CriterionScoreAnalysis
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive check for optional array
  const hasSuggestions = (criterion.improvementSuggestions?.length ?? 0) > 0
  const hasSnippets = (criterion.supportingSnippets?.length ?? 0) > 0
  const hasWork = !!(criterion.scoreRationale || hasSnippets)
  const hasExpandable = hasSuggestions || hasWork

  const ChevronIcon = expanded ? ChevronDown : ChevronRight
  const barWidth = Math.round((criterion.predictedScore / (criterion.maxScore || 5)) * 100)

  function getToggleLabel(): string {
    if (hasWork && hasSuggestions) {
      return t("tender.bidReview.scoring.expandRationaleAndSuggestions")
    }
    if (hasWork) {
      return t("tender.bidReview.scoring.expandRationale")
    }
    return t("tender.bidReview.scoring.expandSuggestions", {
      count: criterion.improvementSuggestions!.length,
    })
  }

  return (
    <div className="bg-muted/20 rounded-lg border px-3 py-2.5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* Header row: name, weight badge, score */}
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium">{criterion.criterionName}</span>
            <Badge className="h-4 px-1.5 text-[10px]" variant="outline">
              {criterion.weightPercent}%
            </Badge>
            <span
              className={cn(
                "ml-auto text-[13px] font-semibold tabular-nums",
                scoreLabelColor(criterion.predictedScore)
              )}
            >
              {criterion.predictedScore}/{criterion.maxScore}
            </span>
          </div>

          {/* Progress bar */}
          <div className="bg-muted mb-2 h-1.5 w-full overflow-hidden rounded-full">
            <div
              style={{ width: `${barWidth}%` }}
              className={cn(
                "h-full rounded-full transition-[width]",
                scoreColor(criterion.predictedScore)
              )}
            />
          </div>

          {/* Formula line — only when backend has computed weighted contribution */}
          {criterion.weightedContribution != null && (
            <p className="text-muted-foreground mb-1.5 text-[11px] tabular-nums">
              {criterion.weightPercent}% <span className="mx-0.5 opacity-60">×</span>{" "}
              {criterion.predictedScore}/{criterion.maxScore}{" "}
              <span className="mx-0.5 opacity-60">=</span>{" "}
              <span className={cn("font-semibold", scoreLabelColor(criterion.predictedScore))}>
                {formatContribution(criterion.weightedContribution)} p
              </span>
            </p>
          )}

          {/* Supporting bid content summary */}
          {criterion.supportingBidContent &&
            (extractionLookup ? (
              <CitationText
                as="p"
                className="text-muted-foreground text-[12px] leading-relaxed italic"
                lookup={extractionLookup}
                text={criterion.supportingBidContent}
                onOpenSource={onOpenSource}
              />
            ) : (
              <p className="text-muted-foreground text-[12px] leading-relaxed italic">
                {criterion.supportingBidContent}
              </p>
            ))}
        </div>
      </div>

      {/* Expand/collapse toggle */}
      {hasExpandable && (
        <Button
          className="text-accent-foreground hover:text-accent-foreground mt-2 h-auto gap-1 p-0 text-[12px]"
          type="button"
          variant="link"
          onClick={() => setExpanded((v) => !v)}
        >
          <ChevronIcon className="size-3" />
          {getToggleLabel()}
        </Button>
      )}

      {expanded && (
        <div className="mt-2.5 flex flex-col gap-3">
          {/* Show-our-work: rationale + source snippets */}
          {hasWork && (
            <div className="flex flex-col gap-2">
              {criterion.scoreRationale &&
                (extractionLookup ? (
                  <CitationText
                    as="p"
                    className="text-muted-foreground text-[12px] leading-relaxed"
                    lookup={extractionLookup}
                    text={criterion.scoreRationale}
                    onOpenSource={onOpenSource}
                  />
                ) : (
                  <p className="text-muted-foreground text-[12px] leading-relaxed">
                    {criterion.scoreRationale}
                  </p>
                ))}
              {hasSnippets && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-muted-foreground text-[11px]">
                    {t("tender.bidReview.scoring.sourcesLabel")}
                  </span>
                  {criterion.supportingSnippets!.map((snippet, i) => (
                    <SourceSnippetPill key={i} snippet={snippet} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Improvement suggestions */}
          {hasSuggestions && (
            <ul className="flex flex-col gap-1 pl-4">
              {criterion.improvementSuggestions.map((suggestion, i) => (
                <li
                  key={i}
                  className="text-accent-foreground list-inside list-disc text-[12px] leading-relaxed"
                >
                  {extractionLookup ? (
                    <CitationText
                      lookup={extractionLookup}
                      text={suggestion}
                      onOpenSource={onOpenSource}
                    />
                  ) : (
                    suggestion
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-14 w-48" />
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function ScoringTab({
  criterionScores,
  totalPredictedScore,
  totalMaxScore,
  scoringNotes,
  isLoading,
  extractionLookup,
  onOpenSource,
}: ScoringTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  const hasScores = (criterionScores?.length ?? 0) > 0

  if (!hasScores) {
    return (
      <p className="text-muted-foreground py-8 text-center text-[13px]">
        {t("tender.bidReview.scoring.empty")}
      </p>
    )
  }

  const showTotal = totalPredictedScore != null && totalMaxScore != null

  return (
    <div className="flex flex-col gap-4">
      {showTotal && (
        <div className="bg-muted/30 rounded-lg border px-4 py-3">
          <p className="text-muted-foreground text-[12px] tracking-wide uppercase">
            {t("tender.bidReview.scoring.totalPredictedLabel")}
          </p>
          <p className="mt-0.5 text-2xl font-bold tabular-nums">
            {totalPredictedScore} / {totalMaxScore}{" "}
            <span className="text-muted-foreground text-sm font-normal">
              {t("tender.bidReview.scoring.pointsSuffix")}
            </span>
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {criterionScores!.map((criterion) => (
          <CriterionRow
            key={criterion.criterionId}
            criterion={criterion}
            extractionLookup={extractionLookup}
            onOpenSource={onOpenSource}
          />
        ))}
      </div>

      {scoringNotes && (
        <div className="text-muted-foreground bg-muted/20 rounded-lg border px-3 py-2.5 text-[12px] leading-relaxed">
          {extractionLookup ? (
            <CitationText
              lookup={extractionLookup}
              text={scoringNotes}
              onOpenSource={onOpenSource}
            />
          ) : (
            scoringNotes
          )}
        </div>
      )}
    </div>
  )
}
